import {computed, inject} from '@angular/core';
import {patchState, signalStore, withComputed, withMethods, withState} from '@ngrx/signals';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {catchError, of, pipe, switchMap, tap} from 'rxjs';
import {AdminMenuItemService} from '../services/admin-menu-item.service';
import {extractErrorMessage} from '@core/utils/error-handler.util';

export interface MenuItem {
  id: number;
  restaurantId: number;
  restaurantName?: string;
  categoryId: number;
  categoryName?: string;
  name: string;
  description?: string;
  price: number;
  originalPrice?: number;
  discountPrice?: number;
  imageUrl?: string;
  available: boolean;
  featured: boolean;
  vegetarian?: boolean;
  vegan?: boolean;
  glutenFree?: boolean;
  outOfStock?: boolean;
  allergens?: string[];
  calories?: number;
  preparationTime?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface MenuCategory {
  id: number;
  restaurantId: number;
  name: string;
}

export interface Restaurant {
  id: number;
  name: string;
}

interface AdminMenuItemState {
  items: MenuItem[];
  categories: MenuCategory[];
  restaurants: Restaurant[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalElements: number;
}

const initialState: AdminMenuItemState = {
  items: [],
  categories: [],
  restaurants: [],
  loading: false,
  saving: false,
  error: null,
  currentPage: 0,
  totalPages: 0,
  totalElements: 0
};

export const AdminMenuItemStore = signalStore(
  {providedIn: 'root'},
  withState(initialState),

  withComputed((state) => ({
    totalItems: computed(() => state.items().length),

    activeItems: computed(() =>
      state.items().filter(i => i.available).length
    ),

    outOfStockItems: computed(() =>
      state.items().filter(i => i.outOfStock).length
    ),

    averagePrice: computed(() => {
      const items = state.items();
      if (items.length === 0) return 0;
      const total = items.reduce((sum, i) => sum + i.price, 0);
      return total / items.length;
    }),

    itemsByCategory: computed(() => {
      const grouped = new Map<number, MenuItem[]>();
      state.items().forEach(item => {
        const existing = grouped.get(item.categoryId) || [];
        grouped.set(item.categoryId, [...existing, item]);
      });
      return grouped;
    })
  })),

  withMethods((store, menuItemService = inject(AdminMenuItemService)) => ({
    loadItems: rxMethod<{ page?: number; restaurantId?: number; categoryId?: number } | void>(
      pipe(
        tap(() => patchState(store, {loading: true, error: null})),
        switchMap((filters) =>
          menuItemService.getItems(filters || {}).pipe(
            tap((response: any) => {
              patchState(store, {
                items: response.content || response || getDemoItems(),
                loading: false,
                currentPage: response.number || 0,
                totalPages: response.totalPages || 1,
                totalElements: response.totalElements || response.length || 0
              });
            }),
            catchError((error) => {
              patchState(store, {
                loading: false,
                error: extractErrorMessage(error),
                items: getDemoItems()
              });
              return of(null);
            })
          )
        )
      )
    ),

    loadRestaurants: rxMethod<void>(
      pipe(
        switchMap(() =>
          menuItemService.getRestaurants().pipe(
            tap((restaurants) => {
              patchState(store, {
                restaurants: restaurants || [
                  {id: 1, name: 'Le Gourmet Parisien'},
                  {id: 2, name: 'Pizza Napoli'},
                  {id: 3, name: 'Sushi Master'}
                ]
              });
            }),
            catchError(() => {
              patchState(store, {
                restaurants: [
                  {id: 1, name: 'Le Gourmet Parisien'},
                  {id: 2, name: 'Pizza Napoli'},
                  {id: 3, name: 'Sushi Master'}
                ]
              });
              return of(null);
            })
          )
        )
      )
    ),

    loadCategories: rxMethod<void>(
      pipe(
        switchMap(() =>
          menuItemService.getCategories().pipe(
            tap((categories) => {
              patchState(store, {
                categories: categories || getDemoCategories()
              });
            }),
            catchError(() => {
              patchState(store, {categories: getDemoCategories()});
              return of(null);
            })
          )
        )
      )
    ),

    createItem: rxMethod<Partial<MenuItem>>(
      pipe(
        tap(() => patchState(store, {saving: true})),
        switchMap((item) =>
          menuItemService.createItem(item).pipe(
            tap((newItem) => {
              const restaurant = store.restaurants().find(r => r.id === Number(item.restaurantId));
              const category = store.categories().find(c => c.id === Number(item.categoryId));
              const items = [...store.items(), {
                ...item,
                id: newItem?.id || Date.now(),
                restaurantName: restaurant?.name,
                categoryName: category?.name,
                createdAt: new Date(),
                updatedAt: new Date()
              } as MenuItem];
              patchState(store, {items, saving: false});
            }),
            catchError((error) => {
              patchState(store, {saving: false, error: extractErrorMessage(error)});
              return of(null);
            })
          )
        )
      )
    ),

    updateItem: rxMethod<MenuItem>(
      pipe(
        tap(() => patchState(store, {saving: true})),
        switchMap((item) =>
          menuItemService.updateItem(item.id, item).pipe(
            tap(() => {
              const restaurant = store.restaurants().find(r => r.id === Number(item.restaurantId));
              const category = store.categories().find(c => c.id === Number(item.categoryId));
              const items = store.items().map(i =>
                i.id === item.id
                  ? {...item, restaurantName: restaurant?.name, categoryName: category?.name, updatedAt: new Date()}
                  : i
              );
              patchState(store, {items, saving: false});
            }),
            catchError((error) => {
              patchState(store, {saving: false, error: extractErrorMessage(error)});
              return of(null);
            })
          )
        )
      )
    ),

    deleteItem: rxMethod<number>(
      pipe(
        switchMap((id) =>
          menuItemService.deleteItem(id).pipe(
            tap(() => {
              const items = store.items().filter(i => i.id !== id);
              patchState(store, {items});
            }),
            catchError((error) => {
              patchState(store, {error: extractErrorMessage(error)});
              return of(null);
            })
          )
        )
      )
    ),

    clearError(): void {
      patchState(store, {error: null});
    }
  }))
);

function getDemoCategories(): MenuCategory[] {
  return [
    {id: 1, restaurantId: 1, name: 'Entrées'},
    {id: 2, restaurantId: 1, name: 'Plats'},
    {id: 3, restaurantId: 1, name: 'Desserts'},
    {id: 4, restaurantId: 2, name: 'Pizzas'},
    {id: 5, restaurantId: 2, name: 'Pâtes'},
    {id: 6, restaurantId: 3, name: 'Sushis'},
    {id: 7, restaurantId: 3, name: 'Makis'}
  ];
}

function getDemoItems(): MenuItem[] {
  return [
    {
      id: 1,
      restaurantId: 1,
      restaurantName: 'Le Gourmet Parisien',
      categoryId: 2,
      categoryName: 'Plats',
      name: 'Filet de bœuf sauce béarnaise',
      description: 'Filet de bœuf tendre accompagné de sa sauce béarnaise maison et de légumes de saison',
      price: 32.50,
      imageUrl: 'https://images.unsplash.com/photo-1558030006-450675393462?w=400',
      available: true,
      featured: true,
      vegetarian: false,
      allergens: ['Lait', 'Œufs'],
      calories: 650,
      preparationTime: 25,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: 2,
      restaurantId: 1,
      restaurantName: 'Le Gourmet Parisien',
      categoryId: 1,
      categoryName: 'Entrées',
      name: 'Foie gras maison',
      description: 'Foie gras de canard mi-cuit, confiture de figues et pain brioché',
      price: 18.00,
      imageUrl: 'https://images.unsplash.com/photo-1625944525533-473f1a3d54e7?w=400',
      available: true,
      featured: false,
      vegetarian: false,
      allergens: ['Gluten'],
      calories: 380,
      preparationTime: 10,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: 3,
      restaurantId: 2,
      restaurantName: 'Pizza Napoli',
      categoryId: 4,
      categoryName: 'Pizzas',
      name: 'Margherita',
      description: 'Sauce tomate, mozzarella di bufala, basilic frais',
      price: 12.00,
      imageUrl: 'https://images.unsplash.com/photo-1574071318508-1cdbab80d002?w=400',
      available: true,
      featured: true,
      vegetarian: true,
      allergens: ['Gluten', 'Lait'],
      calories: 280,
      preparationTime: 15,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: 4,
      restaurantId: 2,
      restaurantName: 'Pizza Napoli',
      categoryId: 4,
      categoryName: 'Pizzas',
      name: 'Quattro Formaggi',
      description: 'Mozzarella, gorgonzola, parmesan, chèvre',
      price: 15.50,
      imageUrl: 'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=400',
      available: true,
      featured: false,
      vegetarian: true,
      allergens: ['Gluten', 'Lait'],
      calories: 420,
      preparationTime: 15,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: 5,
      restaurantId: 3,
      restaurantName: 'Sushi Master',
      categoryId: 6,
      categoryName: 'Sushis',
      name: 'Assortiment Premium',
      description: '12 pièces : saumon, thon, daurade, crevette',
      price: 24.00,
      imageUrl: 'https://images.unsplash.com/photo-1579871494447-9811cf80d66c?w=400',
      available: true,
      featured: true,
      vegetarian: false,
      allergens: ['Poisson', 'Crustacés', 'Soja'],
      calories: 320,
      preparationTime: 20,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: 6,
      restaurantId: 1,
      restaurantName: 'Le Gourmet Parisien',
      categoryId: 3,
      categoryName: 'Desserts',
      name: 'Crème brûlée à la vanille',
      description: 'Crème onctueuse à la vanille bourbon, caramélisée au chalumeau',
      price: 9.50,
      imageUrl: 'https://images.unsplash.com/photo-1470124182917-cc6e71b22ecc?w=400',
      available: true,
      featured: false,
      vegetarian: true,
      allergens: ['Lait', 'Œufs'],
      calories: 280,
      preparationTime: 5,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: 7,
      restaurantId: 3,
      restaurantName: 'Sushi Master',
      categoryId: 7,
      categoryName: 'Makis',
      name: 'California Roll',
      description: 'Avocat, surimi, concombre, mayonnaise japonaise',
      price: 8.50,
      available: false,
      outOfStock: true,
      featured: false,
      vegetarian: false,
      allergens: ['Crustacés', 'Œufs'],
      calories: 180,
      preparationTime: 10,
      createdAt: new Date(),
      updatedAt: new Date()
    }
  ];
}

